import { defineStore } from 'pinia';
import { assessmentModeOptions } from '@/features/assessment/config/assessment-mode.options';
import type { AssessmentMode, AssessmentModeOption } from '@/features/assessment/types/assessment-mode.types';

type EngagementStatus = 'demo' | 'training' | 'live';
type SkinTone = 'asian' | 'black' | 'caucasian';
export type NumericInputMethod = 'slider' | 'tapper';
type DemographicsIdentifierPanel = 'patient_id' | 'visit_id';

interface DemographicsIdentifierConfig {
  panel: DemographicsIdentifierPanel;
  required: boolean;
}

interface ConfigState {
  // engagement
  id: number | null;
  code: string | null;
  name: string | null;
  status: EngagementStatus | null;
  skinTone: SkinTone | null;
  logo: string | null;
  offline: boolean;

  //features
  allowCopyPaste: boolean;
  autoCollectTracks: boolean;
  flexiAutoSave: boolean;
  autoProgress: boolean;
  demographicsIdentifier: DemographicsIdentifierConfig | null;
  editUserInterface: boolean;
  enabledAssessmentModes: AssessmentMode[];
  encounterPauseResume: string | null;
  heartRateInputMethod: NumericInputMethod;
  localGuidelinesDisclaimer: boolean;
  respiratoryRateInputMethod: NumericInputMethod;
  submitOnAssessment: boolean;

  // data export
  publicKey: string | null;
  trustedServers: string | null;
  webhookAuthorization: string | null;
  webhookUrl: string | null;

  // from the same conf.json response; undefined is "never loaded", null is "no address"
  release: Record<string, any>;
  ipAddress: string | null | undefined;
}

const defaultState: ConfigState = {
  // engagement
  id: null,
  code: null,
  name: null,
  status: null,
  logo: null,
  skinTone: null,
  offline: false,

  // features
  allowCopyPaste: false,
  autoCollectTracks: false,
  flexiAutoSave: false,
  autoProgress: false,
  demographicsIdentifier: null,
  editUserInterface: false,
  enabledAssessmentModes: ['text', 'guided'],
  encounterPauseResume: null,
  heartRateInputMethod: 'tapper',
  localGuidelinesDisclaimer: false,
  respiratoryRateInputMethod: 'tapper',
  submitOnAssessment: false,

  // data export
  publicKey: null,
  trustedServers: null,
  webhookAuthorization: null,
  webhookUrl: null,

  release: {},
  ipAddress: undefined,
}

// from conf.json
interface EngagementSettingsConfig {
  code: string;
  engagements_features: {
    allowCopyPaste?: boolean;
    autoCollectTracks?: boolean;
    flexiAutoSave?: boolean;
    autoProgress?: boolean;
    demographicsIdentifier?: DemographicsIdentifierConfig;
    editUserInterface?: boolean;
    enabledAssessmentModes?: AssessmentMode[];
    encounterPauseResume?: string;
    heartRateInputMethod?: NumericInputMethod;
    localGuidelinesDisclaimer?: boolean;
    respiratoryRateInputMethod?: NumericInputMethod;
    submitOnAssessment?: boolean;
  },
  id: number;
  logo: string;
  name: string;
  offline: boolean;
  public_key: string | null;
  skin_tone: SkinTone;
  status: EngagementStatus;
  trusted_servers: string | null;
  webhook_authorization: string | null;
  webhook_url: string | null;
}

type EngagementSettings = Omit<ConfigState, 'release' | 'ipAddress'>;

interface EngagementContextConfig {
  release?: Record<string, any>;
  session?: { ip_address?: string | null };
}

function transformConfigToState(config: EngagementSettingsConfig): EngagementSettings {
  const ef = config.engagements_features
  return {
    id: config.id,
    code: config.code,
    name: config.name,
    status: config.status,
    logo: config.logo,
    skinTone: config.skin_tone,
    offline: config.offline,

    allowCopyPaste: ef?.allowCopyPaste || defaultState.allowCopyPaste,
    autoCollectTracks: ef?.autoCollectTracks || defaultState.autoCollectTracks,
    flexiAutoSave: ef?.flexiAutoSave === true,
    autoProgress: ef?.autoProgress || defaultState.autoProgress,
    demographicsIdentifier: ef?.demographicsIdentifier || defaultState.demographicsIdentifier,
    editUserInterface: ef?.editUserInterface || defaultState.editUserInterface,
    encounterPauseResume: ef?.encounterPauseResume || defaultState.encounterPauseResume,
    heartRateInputMethod: ef?.heartRateInputMethod || defaultState.heartRateInputMethod,
    localGuidelinesDisclaimer: ef?.localGuidelinesDisclaimer || defaultState.localGuidelinesDisclaimer,
    respiratoryRateInputMethod: ef?.respiratoryRateInputMethod || defaultState.respiratoryRateInputMethod,
    submitOnAssessment: ef?.submitOnAssessment || defaultState.submitOnAssessment,
    enabledAssessmentModes: ef?.enabledAssessmentModes || defaultState.enabledAssessmentModes,

    publicKey: config.public_key,
    trustedServers: config.trusted_servers,
    webhookAuthorization: config.webhook_authorization,
    webhookUrl: config.webhook_url,
  }
}

export const usePartnerConfigStore = defineStore('partnerConfig', {
  persist: true,
  state: (): ConfigState => (structuredClone(defaultState)),

  getters: {
    availableAssessmentModes: (state): AssessmentModeOption[] => {
      return assessmentModeOptions.filter(
        (option) => option.enabled && state.enabledAssessmentModes.includes(option.id)
      );
    },
  },

  actions: {
    loadFromConfig(config: EngagementSettingsConfig): void {
      const flatConfig = transformConfigToState(config);
      this.$patch(flatConfig);
    },

    loadContext(conf: EngagementContextConfig): void {
      this.release = conf.release || {};
      this.ipAddress = conf.session?.ip_address;
    },
  },
})
